import React, { useState, useEffect, useCallback } from "react";
import Sidebar, { SidebarProvider } from "../../components/navigation/Sidebar";
import { supabase } from "../../lib/supabase";
import { useModal } from "../../components/ui/ModalProvider";
import { useLanguageProfile } from "../../contexts/LanguageProfileContext";
import { useRequestGuard } from "../../hooks/useRequestGuard";

const FUNCTIONS_URL = import.meta.env.VITE_SUPABASE_FUNCTIONS_URL;
const ANON_KEY      = import.meta.env.VITE_SUPABASE_ANON_KEY;

async function getAccessToken() {
  const { data: { session } } = await supabase.auth.getSession();
  return session?.access_token ?? null;
}

// ─── Small reusable pieces ────────────────────────────────────────────────────

function StatusBadge({ connected }) {
  return connected ? (
    <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold bg-green-100 text-green-700">
      <span className="w-1.5 h-1.5 rounded-full bg-green-500" />
      Connected
    </span>
  ) : (
    <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold bg-amber-100 text-amber-600">
      <span className="w-1.5 h-1.5 rounded-full bg-amber-400" />
      Token Expired
    </span>
  );
}

function Spinner({ size = 5 }) {
  return (
    <svg
      className={`animate-spin h-${size} w-${size}`}
      xmlns="http://www.w3.org/2000/svg"
      fill="none"
      viewBox="0 0 24 24"
    >
      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
    </svg>
  );
}

function ThreadsLogo({ size = 20, color = "#000000" }) {
  return (
    <svg width={size} height={size} viewBox="0 0 192 192" fill={color} aria-hidden="true">
      <path d="M141.537 88.988c-2.406-1.125-4.876-2.107-7.404-2.94-.78-3.169-1.8-6.076-3.034-8.668
        C125.19 62.595 113.617 55.244 99.967 55.244c-9.547 0-18.386 3.463-25.064 9.914
        C68.8 71.01 65.15 79.148 64.374 88.617a54.27 54.27 0 0 0-7.386 2.97l1.944 3.4
        a50.46 50.46 0 0 1 5.876-2.347c-.266 3.44-.186 7.12.298 10.987
        a46.06 46.06 0 0 0 2.352 9.196c6.726 18.278 23.41 28.98 43.77 28.98
        c7.822 0 15.006-1.82 21.345-5.374a37.22 37.22 0 0 0 8.673-7.0
        c5.047-5.69 8.118-13.104 8.273-21.397a64.33 64.33 0 0 1 5.666 2.008l2.155-3.762
        c-7.46-3.015-15.614-4.798-23.993-4.29zm-41.57 31.23
        c-16.624 0-29.546-9.223-35.18-24.443a39.96 39.96 0 0 1-1.927-7.317
        c-.628-4.347-.458-8.535.268-12.398
        c1.16-6.368 3.892-11.672 7.926-15.433
        C76.096 56.15 87.035 51.46 105.967 51.46
        c17.7 0 30.215 8.147 36.166 22.785.842 2.043 1.576 4.163 2.196 6.346
        a80.58 80.58 0 0 0-11.34-.509c-5.875 0-11.388.534-16.422 1.569v4.106
        c4.873-1.018 10.212-1.548 15.904-1.548 4.444 0 8.75.362 12.889 1.042
        .263 4.262-.208 8.326-1.359 12.026-1.157 3.803-3.083 7.172-5.68 9.967
        a30.47 30.47 0 0 1-6.975 5.685c-5.247 3.022-11.487 4.618-18.544 4.618z"/>
    </svg>
  );
}

// ─── Main page ────────────────────────────────────────────────────────────────

export default function ThreadsAccounts() {
  const { alert, confirm } = useModal();
  const { activeProfile } = useLanguageProfile();
  const startAccountsRequest = useRequestGuard();

  const [user, setUser]           = useState(null);
  const [accounts, setAccounts]   = useState([]);
  const [loading, setLoading]     = useState(true);
  const [refreshingId, setRefreshingId] = useState(null);
  const [refreshResults, setRefreshResults] = useState({});

  // ── Read URL params (callback redirects back here) ─────────────────────────
  const params    = new URLSearchParams(window.location.search);
  const connected = params.get("connected");
  const error     = params.get("error");
  const errorDetail = params.get("detail");

  // ── Auth ──────────────────────────────────────────────────────────────────
  useEffect(() => {
    supabase.auth.getUser().then(({ data }) => setUser(data?.user ?? null));
  }, []);

  // ── Show toast after OAuth redirect ───────────────────────────────────────
  useEffect(() => {
    if (connected === "true") alert("✅ Threads account connected successfully!");
    if (error) {
      const detailSuffix = errorDetail ? `\n\nDetails: ${errorDetail}` : "";
      alert(`❌ Connection failed: ${friendlyError(error)}${detailSuffix}`);
    }
  }, []); // eslint-disable-line

  // Clear the previous profile's accounts immediately on switch so stale
  // content can't flash while the new profile's data is loading.
  useEffect(() => {
    setAccounts([]);
  }, [activeProfile?.id]);

  // ── Load accounts ─────────────────────────────────────────────────────────
  const loadAccounts = useCallback(async () => {
    if (!user) return;
    const isCurrent = startAccountsRequest();
    setLoading(true);
    try {
      const token = await getAccessToken();
      if (!token) { if (isCurrent()) setLoading(false); return; }

      const accountsUrl = activeProfile?.id
        ? `${FUNCTIONS_URL}/threads-accounts?profileId=${encodeURIComponent(activeProfile.id)}`
        : `${FUNCTIONS_URL}/threads-accounts`;
      const res  = await fetch(accountsUrl, {
        headers: { Authorization: `Bearer ${token}`, apikey: ANON_KEY },
      });
      const json = await res.json();
      if (!isCurrent()) return;
      setAccounts(json.accounts ?? []);
    } catch (e) {
      if (!isCurrent()) return;
      console.error("[ThreadsAccounts] Failed to load accounts:", e);
    } finally {
      if (isCurrent()) setLoading(false);
    }
  }, [user, activeProfile?.id, startAccountsRequest]);

  useEffect(() => { loadAccounts(); }, [loadAccounts]);

  // Clean URL params after load
  useEffect(() => {
    if (connected || error) {
      window.history.replaceState({}, "", window.location.pathname);
    }
  }, [connected, error]);

  // ── Connect new account ────────────────────────────────────────────────────
  const handleConnect = async () => {
    if (!user) { alert("Please sign in first."); return; }
    try {
      const token = await getAccessToken();
      const authUrl = activeProfile?.id
        ? `${FUNCTIONS_URL}/threads-auth-url?profileId=${encodeURIComponent(activeProfile.id)}`
        : `${FUNCTIONS_URL}/threads-auth-url`;
      const res   = await fetch(authUrl, {
        headers: { Authorization: `Bearer ${token}`, apikey: ANON_KEY },
      });
      const json = await res.json();
      if (!res.ok || !json.url) throw new Error(json.error ?? "Failed to start OAuth");
      window.location.href = json.url;
    } catch (e) {
      alert(`❌ Could not connect: ${e.message}`);
    }
  };

  // ── Delete account ─────────────────────────────────────────────────────────
  const handleDelete = async (account) => {
    const display = account.username ? `@${account.username}` : account.name || "this account";
    if (!(await confirm(`Disconnect ${display} from Gymplix?`))) return;
    try {
      const token = await getAccessToken();
      await fetch(`${FUNCTIONS_URL}/threads-accounts`, {
        method:  "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization:   `Bearer ${token}`,
          apikey:          ANON_KEY,
        },
        body: JSON.stringify({ action: "delete", accountId: account.id }),
      });
      setAccounts((prev) => prev.filter((a) => a.id !== account.id));
    } catch (e) {
      alert("❌ Failed to disconnect. Please try again.");
    }
  };

  // ── Refresh token ──────────────────────────────────────────────────────────
  const handleRefresh = async (account) => {
    setRefreshingId(account.id);
    setRefreshResults((prev) => ({ ...prev, [account.id]: null }));
    try {
      const token = await getAccessToken();
      const res   = await fetch(`${FUNCTIONS_URL}/threads-accounts`, {
        method:  "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization:   `Bearer ${token}`,
          apikey:          ANON_KEY,
        },
        body: JSON.stringify({ action: "refresh", accountId: account.id }),
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json.error ?? "Refresh failed");
      setRefreshResults((prev) => ({
        ...prev,
        [account.id]: { expiresAt: json.token_expires_at },
      }));
      // Update the local account list with new expiry
      setAccounts((prev) =>
        prev.map((a) =>
          a.id === account.id ? { ...a, token_expires_at: json.token_expires_at } : a
        )
      );
    } catch (e) {
      setRefreshResults((prev) => ({ ...prev, [account.id]: { error: e.message } }));
    } finally {
      setRefreshingId(null);
    }
  };

  // ─────────────────────────────────────────────────────────────────────────

  return (
    <SidebarProvider>
      <div className="min-h-screen bg-gray-50">
        <Sidebar />

        <main className="main-content">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 py-8 space-y-6">

            {/* Header */}
            <div className="bg-white border border-gray-200 rounded-xl p-5 flex items-center justify-between gap-4">
              <div className="flex items-center gap-3">
                <div
                  className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0"
                  style={{ backgroundColor: "#F3F3F3" }}
                >
                  <ThreadsLogo size={22} />
                </div>
                <div>
                  <h1 className="text-lg font-bold text-gray-900">Threads Accounts</h1>
                  <p className="text-xs text-gray-500">
                    Connect your Threads account to manage content from one place.
                  </p>
                </div>
              </div>
              <StatusBadge connected={accounts.length > 0 && accounts.some((a) => !isTokenExpired(a))} />
            </div>

            {/* Body */}
            {loading ? (
              <div className="flex items-center justify-center py-20 text-gray-400">
                <Spinner size={8} />
              </div>
            ) : accounts.length === 0 ? (
              <EmptyState onConnect={handleConnect} />
            ) : (
              <div className="space-y-4">
                {accounts.map((account) => (
                  <AccountCard
                    key={account.id}
                    account={account}
                    refreshResult={refreshResults[account.id]}
                    isRefreshing={refreshingId === account.id}
                    onRefresh={handleRefresh}
                    onDelete={handleDelete}
                  />
                ))}

                {/* Add another account */}
                <button
                  type="button"
                  onClick={handleConnect}
                  className="w-full border-2 border-dashed border-gray-300 rounded-xl p-6 text-center
                             hover:border-gray-500 hover:bg-gray-100 transition-colors group"
                >
                  <span className="text-2xl group-hover:scale-110 inline-block transition-transform text-gray-400 group-hover:text-gray-700">+</span>
                  <p className="text-sm text-gray-500 mt-1 group-hover:text-gray-700">
                    Connect another Threads account
                  </p>
                </button>
              </div>
            )}

          </div>
        </main>
      </div>
    </SidebarProvider>
  );
}

// ─── Sub-components ───────────────────────────────────────────────────────────

function EmptyState({ onConnect }) {
  return (
    <div className="bg-white border border-gray-200 rounded-xl p-12 text-center">
      <div
        className="w-16 h-16 rounded-full mx-auto mb-4 flex items-center justify-center"
        style={{ backgroundColor: "#F3F3F3" }}
      >
        <ThreadsLogo size={32} />
      </div>
      <h2 className="text-lg font-semibold text-gray-900">No Threads Account Connected</h2>
      <p className="text-sm text-gray-500 mt-2 max-w-sm mx-auto">
        Connect your Threads account to manage and publish content directly from Gymplix.
      </p>
      <button
        type="button"
        onClick={onConnect}
        className="mt-6 px-6 py-2.5 rounded-lg text-white text-sm font-semibold transition-colors"
        style={{ backgroundColor: "#101010" }}
        onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "#333")}
        onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "#101010")}
      >
        Connect Threads Account
      </button>
    </div>
  );
}

function AccountCard({ account, refreshResult, isRefreshing, onRefresh, onDelete }) {
  const expired       = isTokenExpired(account);
  const connectedDate = account.connected_at
    ? new Date(account.connected_at).toLocaleDateString(undefined, {
        year: "numeric", month: "long", day: "numeric",
      })
    : "—";
  const expiresDate = account.token_expires_at
    ? new Date(account.token_expires_at).toLocaleDateString(undefined, {
        year: "numeric", month: "long", day: "numeric",
      })
    : "—";

  return (
    <div className="bg-white border border-gray-200 rounded-xl p-5 shadow-sm">
      {/* Top row */}
      <div className="flex items-start gap-4">
        {/* Avatar */}
        {account.profile_picture_url ? (
          <img
            src={account.profile_picture_url}
            alt={account.username || "Threads"}
            className="w-12 h-12 rounded-full flex-shrink-0 object-cover border border-gray-200"
          />
        ) : (
          <div
            className="w-12 h-12 rounded-full flex-shrink-0 flex items-center justify-center border border-gray-200"
            style={{ backgroundColor: "#F3F3F3" }}
          >
            <ThreadsLogo size={22} />
          </div>
        )}

        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className="font-semibold text-gray-900 text-base">
              {account.name || account.username || "Threads Account"}
            </h3>
            <StatusBadge connected={!expired} />
            {expired && (
              <span className="text-xs text-amber-600 font-medium">
                Token expired — refresh or reconnect
              </span>
            )}
          </div>

          <dl className="mt-2 grid grid-cols-2 gap-x-6 gap-y-1 text-xs text-gray-500">
            {account.username && (
              <div className="flex gap-1.5">
                <dt className="font-medium text-gray-600">Username:</dt>
                <dd>@{account.username}</dd>
              </div>
            )}
            <div className="flex gap-1.5">
              <dt className="font-medium text-gray-600">Connected:</dt>
              <dd>{connectedDate}</dd>
            </div>
            <div className="flex gap-1.5">
              <dt className="font-medium text-gray-600">Token expires:</dt>
              <dd>{expiresDate}</dd>
            </div>
          </dl>
        </div>
      </div>

      {/* Refresh result banner */}
      {refreshResult && (
        <div
          className={`mt-3 rounded-lg px-4 py-2 text-sm ${
            refreshResult.error
              ? "bg-red-50 text-red-700 border border-red-200"
              : "bg-green-50 text-green-700 border border-green-200"
          }`}
        >
          {refreshResult.error
            ? `Refresh failed: ${refreshResult.error}`
            : `✅ Token refreshed successfully`}
        </div>
      )}

      {/* Action buttons */}
      <div className="mt-4 flex flex-wrap items-center gap-2 border-t border-gray-100 pt-4">
        <button
          type="button"
          onClick={() => onRefresh(account)}
          disabled={isRefreshing}
          className="flex items-center gap-1.5 px-3 py-1.5 rounded-md border text-sm font-medium
                     transition-colors disabled:opacity-50 disabled:cursor-not-allowed border-gray-300 text-gray-700 hover:bg-gray-50"
        >
          {isRefreshing ? <><Spinner size={4} /> Refreshing...</> : "Refresh Token"}
        </button>

        <button
          type="button"
          onClick={() => onDelete(account)}
          className="ml-auto px-3 py-1.5 rounded-md text-sm text-red-600 hover:bg-red-50
                     transition-colors font-medium border border-red-200"
        >
          Disconnect
        </button>
      </div>
    </div>
  );
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

function isTokenExpired(account) {
  if (!account.token_expires_at) return false;
  return new Date(account.token_expires_at) < new Date();
}

function friendlyError(code) {
  const map = {
    cancelled:      "You cancelled the connection.",
    invalid_state:  "Session mismatch — please try again.",
    token_exchange: "Token exchange with Threads failed.",
    profile_fetch:  "Could not retrieve your Threads profile.",
    db_save:        "Failed to save account — please try again.",
    config:         "Threads OAuth is not configured yet.",
    server_error:   "An unexpected server error occurred.",
    missing_params: "Invalid OAuth response from Threads.",
  };
  return map[code] ?? code;
}
